import React from 'react';
import { Container, Typography, Box, Paper, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import VideoCameraBackIcon from '@mui/icons-material/VideoCameraBack';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import ReceiptIcon from '@mui/icons-material/Receipt';
import InsightsIcon from '@mui/icons-material/Insights';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import { DESIGN_TOKENS } from '../theme/designTokens';

const PageContainer = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(DESIGN_TOKENS.spacing.section.lg),
  paddingBottom: theme.spacing(DESIGN_TOKENS.spacing.section.lg),
  background: 'linear-gradient(135deg, #FEFEFE 0%, #F9FAFB 100%)',
  minHeight: '100vh',
  [theme.breakpoints.down('md')]: {
    paddingTop: theme.spacing(DESIGN_TOKENS.spacing.section.md),
    paddingBottom: theme.spacing(DESIGN_TOKENS.spacing.section.md),
  },
  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(DESIGN_TOKENS.spacing.section.sm),
    paddingBottom: theme.spacing(DESIGN_TOKENS.spacing.section.sm),
  },
}));

const StepCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  background: '#FEFEFE',
  border: '1px solid #E5E7EB',
  borderRadius: DESIGN_TOKENS.borderRadius.lg,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-8px)',
    borderColor: '#4338CA',
    '& .step-number': {
      background: 'linear-gradient(135deg, #4338CA 0%, #6D28D9 100%)',
      transform: 'scale(1.1)',
    },
    '& .step-icon': {
      color: '#4338CA',
      transform: 'scale(1.05)',
    },
  },
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(4),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  },
}));

const StepNumber = styled(Box)(({ theme }) => ({
  width: 56,
  height: 56,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #9CA3AF 0%, #6B7280 100%)',
  color: '#ffffff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
  fontSize: '1.5rem',
  fontWeight: 700,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  [theme.breakpoints.down('sm')]: {
    width: 48,
    height: 48,
    fontSize: '1.25rem',
  },
}));

const StepsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: theme.spacing(4),
  marginTop: theme.spacing(8),
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: theme.spacing(3),
    marginTop: theme.spacing(6),
  },
}));

const FeaturesGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: theme.spacing(4),
  marginTop: theme.spacing(8),
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: theme.spacing(3),
    marginTop: theme.spacing(6),
  },
}));

const FeatureCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'center',
  background: '#FEFEFE',
  border: '1px solid #E5E7EB',
  borderRadius: DESIGN_TOKENS.borderRadius.lg,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-4px)',
    borderColor: '#6D28D9',
  },
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(4),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  },
}));

const HowItWorks = () => {
  const { t } = useTranslation();
  const featureCards = t('howItWorks.features.cards', { returnObjects: true }) as Record<string, string>;
  
  // Set page title for analytics
  usePageTitle('How It Works');

  return (
    <PageContainer>
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Typography 
            variant="h1" 
            component="h1" 
            align="center" 
            gutterBottom
            sx={{
              fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem', lg: '4rem' },
              fontWeight: 700,
              background: 'linear-gradient(135deg, #111827 0%, #374151 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: 3,
            }}
          >
            {t('howItWorks.title')}
          </Typography>
          <Typography 
            variant="h2" 
            component="h2" 
            align="center" 
            paragraph
            sx={{
              color: '#6B7280',
              fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
              lineHeight: 1.6,
              maxWidth: '800px',
              margin: '0 auto',
            }}
          >
            {t('howItWorks.subtitle')}
          </Typography>
        </motion.div>

        <Box mt={{ xs: 8, md: 12 }} component="section" role="region" aria-labelledby="steps-heading">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <Typography 
              id="steps-heading"
              variant="h2" 
              component="h2" 
              align="center" 
              gutterBottom
              sx={{
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                fontWeight: 700,
                color: '#111827',
                marginBottom: 2,
              }}
            >
              {t('howItWorks.process.title')}
            </Typography>
            <Typography 
              variant="h3" 
              component="h3" 
              align="center" 
              paragraph
              sx={{
                color: '#6B7280',
                fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                maxWidth: '600px',
                margin: '0 auto',
                lineHeight: 1.6,
              }}
            >
              {t('howItWorks.process.subtitle')}
            </Typography>
          </motion.div>

          <StepsGrid>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <StepCard>
                <StepNumber className="step-number">1</StepNumber>
                <VideoCameraBackIcon 
                  className="step-icon"
                  sx={{ 
                    fontSize: 56, 
                    color: '#9CA3AF', 
                    mb: 3,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }} 
                  aria-hidden="true"
                />
                <Typography 
                  variant="h4" 
                  component="h4" 
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    color: '#111827',
                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                    mb: 2,
                  }}
                >
                  {t('howItWorks.steps.1.title')}
                </Typography>
                <Typography 
                  sx={{
                    color: '#6B7280',
                    lineHeight: 1.6,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  }}
                >
                  {t('howItWorks.steps.1.description')}
                </Typography>
              </StepCard>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <StepCard>
                <StepNumber className="step-number">2</StepNumber>
                <AnalyticsIcon 
                  className="step-icon"
                  sx={{ 
                    fontSize: 56, 
                    color: '#9CA3AF', 
                    mb: 3,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }} 
                  aria-hidden="true"
                />
                <Typography 
                  variant="h4" 
                  component="h4" 
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    color: '#111827',
                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                    mb: 2,
                  }}
                >
                  {t('howItWorks.steps.2.title')}
                </Typography>
                <Typography 
                  sx={{
                    color: '#6B7280',
                    lineHeight: 1.6,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  }}
                >
                  {t('howItWorks.steps.2.description')}
                </Typography>
              </StepCard>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <StepCard>
                <StepNumber className="step-number">3</StepNumber>
                <ReceiptIcon 
                  className="step-icon"
                  sx={{ 
                    fontSize: 56, 
                    color: '#9CA3AF', 
                    mb: 3,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }} 
                  aria-hidden="true"
                />
                <Typography 
                  variant="h4" 
                  component="h4" 
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    color: '#111827',
                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                    mb: 2,
                  }}
                >
                  {t('howItWorks.steps.3.title')}
                </Typography>
                <Typography 
                  sx={{
                    color: '#6B7280',
                    lineHeight: 1.6,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  }}
                >
                  {t('howItWorks.steps.3.description')}
                </Typography>
              </StepCard>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <StepCard>
                <StepNumber className="step-number">4</StepNumber>
                <InsightsIcon 
                  className="step-icon"
                  sx={{ 
                    fontSize: 56, 
                    color: '#9CA3AF', 
                    mb: 3,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }} 
                  aria-hidden="true"
                />
                <Typography 
                  variant="h4" 
                  component="h4" 
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    color: '#111827',
                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                    mb: 2,
                  }}
                >
                  {t('howItWorks.steps.4.title')}
                </Typography>
                <Typography 
                  sx={{
                    color: '#6B7280',
                    lineHeight: 1.6,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                  }}
                >
                  {t('howItWorks.steps.4.description')}
                </Typography>
              </StepCard>
            </motion.div>
          </StepsGrid>
        </Box>

        <Box mt={{ xs: 8, md: 12 }} component="section" role="region" aria-labelledby="features-heading">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <Typography 
              id="features-heading"
              variant="h2" 
              component="h2" 
              align="center" 
              gutterBottom
              sx={{
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                fontWeight: 700,
                color: '#111827',
                marginBottom: 2,
              }}
            >
              {t('howItWorks.features.title')}
            </Typography>
            <Typography 
              variant="h3" 
              component="h3" 
              align="center" 
              paragraph
              sx={{
                color: '#6B7280',
                fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                maxWidth: '800px',
                margin: '0 auto',
                lineHeight: 1.6,
              }}
            >
              {t('howItWorks.features.subtitle')}
            </Typography>
          </motion.div>

          <FeaturesGrid>
            {Object.entries(featureCards).map(([key, value], index) => (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: 0.1 * (index + 1) }}
              >
                <FeatureCard>
                  <Typography 
                    sx={{
                      fontWeight: 600,
                      color: '#111827',
                      fontSize: { xs: '1.125rem', sm: '1.25rem' },
                    }}
                  >
                    {value}
                  </Typography>
                </FeatureCard>
              </motion.div>
            ))}
          </FeaturesGrid>
        </Box>

        <Box mt={{ xs: 8, md: 12 }}>
          <Paper 
            sx={{ 
              p: { xs: 3, sm: 6 }, 
              textAlign: 'center',
              borderRadius: 4,
              background: 'linear-gradient(135deg, rgba(94, 106, 210, 0.04) 0%, rgba(139, 92, 246, 0.04) 100%)',
              border: '1px solid rgba(94, 106, 210, 0.1)',
            }}
          >
            <Typography 
              variant="h2" 
              component="h2" 
              gutterBottom
              sx={{
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                fontWeight: 700,
                color: '#111827',
                marginBottom: 2,
              }}
            >
              {t('howItWorks.cta.title')}
            </Typography>
            <Typography 
              variant="body1" 
              paragraph
              sx={{ 
                maxWidth: '600px', 
                mx: 'auto', 
                mb: 4,
                color: '#6B7280',
                lineHeight: 1.7,
              }}
            >
              {t('howItWorks.cta.description')}
            </Typography>
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button 
                variant="contained" 
                size="large"
                component={RouterLink}
                to="/upload"
                sx={{ 
                  px: 4,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #5E6AD2 0%, #8B5CF6 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #4C5BC7 0%, #7C3AED 100%)',
                  }
                }}
              >
                {t('howItWorks.cta.button')}
              </Button>
            </motion.div>
          </Paper>
        </Box>
      </Container>
    </PageContainer>
  );
};

export default HowItWorks; 